import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type FieldWorkClassificationCodeListItemVM from 'ab-viewModels/fieldWorkClassificationCode/listItem';
import type TimeSheetEntryWithAccountVM from 'ab-viewModels/timeSheet/timeCardEntry.viewModel';
import type TimeCardRejectedTimeSheetVM from 'ab-viewModels/timeSheet/timeCardRejectedTimeSheet.viewModel';
import type { TimeSheetVM } from 'ab-viewModels/timeSheet/timeSheet.viewModel';
import type { TimeSheetEntriesForTimelineVM } from 'ab-viewModels/timeSheet/timeSheetEntry.viewModel';
import type TimeSheetTrackedEntriesVM from 'ab-viewModels/timeSheet/trackedEntries.viewModel';
import type { EquipmentListItemVM } from 'ab-viewModels/timeSplitEquipment/timeSplitEquipment';
import type { SubjobVM } from 'ab-viewModels/workRequest/jobUpsert.viewModel';

import type { TimeSheetsBulkUpdateRM } from 'ab-requestModels/timeSheet/timeSheetUpdate';
import type TimeSheetUpdateRM from 'ab-requestModels/timeSheet/timeSheetUpdate';

import { getToLookupByKey } from '@acceligentllc/shared/utils/array';
import { isAllowed } from '@acceligentllc/shared/utils/auth';
import * as TimeSheetUtils from '@acceligentllc/shared/utils/timeSheet';

import { FieldReportAccessRole } from '@acceligentllc/shared/enums/fieldReportAccessRole';
import { TimeSheetApprovalStatus } from '@acceligentllc/shared/enums/timeSheetApprovalStatus';
import type { WorkOrderReviewStatus, WorkOrderReviewLevel } from '@acceligentllc/shared/enums/workOrderReviewStatus';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';
import type TimeZoneSourceType from 'ab-enums/timezoneSourceType.enum';

import TimezoneSourceBanner from 'af-components/TimezoneSourceBanner';

import Loading from './Loading';
import styles from './styles.module.scss';
import type { TimeSheetAvailableActions } from '../types';
import TimeSheetsEditModal, { BulkOrSingleEditModal } from './TimeSheetEditModal';
import TimeSheetRow from './TimeSheetRow/TimeSheetRow';
import TimeSheetVirtualRow from './TimeSheetVirtualRow';
import type SignatureForm from '../../Shared/SignatureModal/FormModel';

interface TimeSheetWithPerDiem extends TimeSheetVM {
	perDiemInfo: {
		perDiemFlag: boolean;
		perDiemAmount: Nullable<string>;
	};
}

interface OwnProps {
	accessRole: Nullable<FieldReportAccessRole>;
	dueDate: string;
	code: string;
	workOrderId: number;
	workRequestId: number;
	timeSheets: TimeSheetWithPerDiem[];
	loading: boolean;
	activeAccounts: { [accountId: number]: true; };
	temporaryEmployeeAccounts: { [temporaryEmployeeId: number]: true; };
	areReportsReadOnly: boolean;
	areReportsFinalOrDone: boolean;
	onTimeSheetSubmit: (form: SignatureForm) => Promise<void>;
	onTimeSheetSave: (workOrderId: number, accountId: number, data: TimeSheetUpdateRM) => Promise<void>;
	workOrderReviewStatus: WorkOrderReviewStatus;
	workOrderReviewLevel: WorkOrderReviewLevel;
	trackedEntries: Nullable<TimeSheetTrackedEntriesVM>;
	timeSheetEntriesForTimeline: Nullable<TimeSheetEntriesForTimelineVM>;
	timeZoneInUse: Nullable<string>;
	timezoneSourceType: Nullable<TimeZoneSourceType>;
	timeZone: Nullable<string>;
	changeTimeZone: () => void;
	isPaused: boolean;
	hasSIUnassignedPermission: boolean;
	classificationCodes: FieldWorkClassificationCodeListItemVM[];
	sections: Nullable<TimeSplitEntryEquipmentSections>[];
	canSeeTimeAllocations: boolean;
	canManagePerDiem: boolean;
	associatedWRId: Nullable<number>;
	entriesGroupedByAccountId: Record<number, TimeSheetEntryWithAccountVM[]>;
	rejectedTimeSheetsForTimeCards: TimeCardRejectedTimeSheetVM[];
	onUpdateTimeSheetEntry: () => void;
	isProjectOrSubjob: boolean;
	subjobsForAllocation: SubjobVM[];
	loadSubjobsForAllocation: () => Promise<void>;
	timeSheetsAvailableActions: TimeSheetAvailableActions[];
	shouldRoundTo1Min: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

type DuplicateAccumulator = {
	duplicates: { [name: string]: true; };
	tempEmpNames: { [name: string]: true; };
};

type TimeSplitEntryEquipmentSections = {
	title: string;
	options: EquipmentListItemVM[];
};

const duplicateReducer = (acc: DuplicateAccumulator, timeSheet: TimeSheetVM) => {
	if (timeSheet.temporaryEmployeeUniqueId) {
		if (acc.tempEmpNames[timeSheet.userFullName]) {
			acc.duplicates[timeSheet.userFullName] = true;
		} else {
			acc.tempEmpNames[timeSheet.userFullName] = true;
		}
	}
	return acc;
};

const TimeSheets: React.FC<Props> = (props) => {
	const {
		accessRole,
		dueDate,
		code,
		workOrderId,
		timeSheets,
		loading,
		activeAccounts,
		temporaryEmployeeAccounts,
		areReportsReadOnly,
		areReportsFinalOrDone,
		canSeeTimeAllocations,
		onTimeSheetSubmit,
		onTimeSheetSave,
		workOrderReviewStatus,
		workOrderReviewLevel,
		trackedEntries,
		timeSheetEntriesForTimeline,
		timeZoneInUse,
		timezoneSourceType,
		timeZone,
		changeTimeZone,
		accountId,
		isManagerOrAdmin,
		isManagementAllowedToEdit,
		isPaused,
		hasSIUnassignedPermission,
		classificationCodes,
		sections,
		workRequestId,
		associatedWRId,
		entriesGroupedByAccountId,
		rejectedTimeSheetsForTimeCards,
		isAccountingOrAdmin,
		onUpdateTimeSheetEntry,
		canManagePerDiem,
		isProjectOrSubjob,
		loadSubjobsForAllocation,
		subjobsForAllocation,
		timeSheetsAvailableActions,
		shouldRoundTo1Min,
	} = props;

	const isSuperintendent: boolean = accessRole === FieldReportAccessRole.SUPERINTENDENT || hasSIUnassignedPermission;

	const [timeSheetInEdit, setTimeSheetInEdit] = React.useState<Nullable<TimeSheetWithPerDiem>>(null);
	const [continueToSignatureModalForTimeSheet, setContinueToSignatureModalForTimeSheet] = React.useState<Nullable<number>>(null);

	const {
		duplicates,
	} = React.useMemo(() => {
		return timeSheets.reduce(duplicateReducer, { duplicates: {}, tempEmpNames: {} } as DuplicateAccumulator);
	}, [timeSheets]);

	const availableActionsLookup = React.useMemo(() => {
		return timeSheetsAvailableActions.reduce(getToLookupByKey((_tsA) => _tsA.timeSheetId), {});
	}, [timeSheetsAvailableActions]);

	const closeEditModal = React.useCallback(() => setTimeSheetInEdit(null), []);

	const openEditModal = React.useCallback((timeSheet: TimeSheetWithPerDiem) => setTimeSheetInEdit(timeSheet), []);

	const onSaveTimeSheetEditing = React.useCallback(async (_workOrderId: number, data: TimeSheetsBulkUpdateRM) => {
		if (timeSheetInEdit) {
			if (Object.keys(data).length === 1) {
				const employeeId = Object.keys(data)[0];
				await onTimeSheetSave(_workOrderId, timeSheetInEdit.accountId, data[employeeId]);
			}
		} else {
			throw new Error('Can not save this time sheet');
		}
	}, [onTimeSheetSave, timeSheetInEdit]);

	const onConfirmEndShift = React.useCallback((timeSheetId: Nullable<number>) => {
		setContinueToSignatureModalForTimeSheet(timeSheetId);
	}, []);

	const renderTimeSheetRow = React.useCallback((timeSheet: TimeSheetWithPerDiem) => {
		const isOwner = timeSheet.accountId === accountId;

		const isAllowedToEditTimeSheet = TimeSheetUtils.isUserAllowedToEdit(
			timeSheet?.accountId === accountId,
			isManagerOrAdmin,
			isSuperintendent,
			isManagementAllowedToEdit
		);

		const isTimeSheetReadonly = !!timeSheet.superintendentApprovalStatus?.approvalStatus && TimeSheetUtils.shouldTsBeReadOnly(
			workOrderReviewStatus,
			timeSheet.superintendentApprovalStatus?.approvalStatus,
			isAllowedToEditTimeSheet
		);

		const trackedEntry = trackedEntries?.entries?.find((entry) => entry.accountId === timeSheet.accountId) ?? null;

		const entries = timeSheetEntriesForTimeline?.timelineEntitiesAndOverlaps?.[timeSheet.accountId];
		const accountTimeSheetEntries = entries?.timelineEntities;
		const accountOverlaps = entries?.overlaps ?? null;
		const hasEntryChanges = entries?.hasChanges;

		const isDuplicate = !!duplicates[timeSheet.userFullName];

		const allAccountEntries: TimeSheetEntryWithAccountVM[] = entriesGroupedByAccountId[timeSheet.accountId];

		return (
			<TimeSheetRow
				active={activeAccounts[timeSheet.accountId]}
				allAccountEntries={allAccountEntries}
				areReportsReadOnly={areReportsReadOnly || areReportsFinalOrDone}
				canManagePerDiem={canManagePerDiem}
				canReject={availableActionsLookup[timeSheet.id]?.canReject ?? false}
				code={code}
				continueToSignatureModalForTimeSheet={continueToSignatureModalForTimeSheet}
				hasEntryChanges={hasEntryChanges ?? false}
				isAccountingOrAdmin={isAccountingOrAdmin}
				isDuplicateName={isDuplicate}
				isManagerOrAdmin={isManagerOrAdmin}
				isOwner={isOwner}
				isSuperintendent={isSuperintendent}
				isTemporaryEmployee={temporaryEmployeeAccounts[timeSheet.accountId] ?? false}
				isTimeSheetReadOnly={isTimeSheetReadonly || isPaused}
				key={timeSheet.accountId}
				onEdit={openEditModal}
				onEndTimeShift={onConfirmEndShift}
				onTimeSheetSubmit={onTimeSheetSubmit}
				onUpdateTimeSheetEntry={onUpdateTimeSheetEntry}
				rejectedTimeSheetsForTimeCards={rejectedTimeSheetsForTimeCards}
				shouldRoundTo1Min={shouldRoundTo1Min}
				timeSheet={timeSheet}
				timeSheetEntities={accountTimeSheetEntries ?? []}
				timeSheetOverlaps={accountOverlaps}
				timeZoneInUse={timeZoneInUse}
				timezoneSourceType={timezoneSourceType}
				trackedEntry={trackedEntry}
				workOrderId={workOrderId}
				workOrderReviewStatus={workOrderReviewStatus}
			/>
		);
	}, [
		accountId,
		isManagerOrAdmin,
		isSuperintendent,
		isAccountingOrAdmin,
		isManagementAllowedToEdit,
		canManagePerDiem,
		workOrderReviewStatus,
		trackedEntries?.entries,
		timeSheetEntriesForTimeline?.timelineEntitiesAndOverlaps,
		duplicates,
		entriesGroupedByAccountId,
		activeAccounts,
		areReportsReadOnly,
		areReportsFinalOrDone,
		code,
		temporaryEmployeeAccounts,
		isPaused,
		openEditModal,
		onTimeSheetSubmit,
		timeZoneInUse,
		timezoneSourceType,
		workOrderId,
		rejectedTimeSheetsForTimeCards,
		onUpdateTimeSheetEntry,
		availableActionsLookup,
		shouldRoundTo1Min,
		onConfirmEndShift,
		continueToSignatureModalForTimeSheet,
	]);

	const renderVirtualTimeSheetRow = React.useCallback((timeSheet: TimeSheetVM) => {

		const isOwner = timeSheet.accountId === accountId;

		const entries = timeSheetEntriesForTimeline?.timelineEntitiesAndOverlaps?.[timeSheet.accountId];
		const accountTimeSheetEntries = entries?.timelineEntities;
		const accountOverlaps = entries?.overlaps ?? null;

		return (
			<TimeSheetVirtualRow
				associatedJobId={associatedWRId!}
				isManagerOrAdmin={isManagerOrAdmin}
				isOwner={isOwner}
				isSuperintendent={isSuperintendent}
				isTemporaryEmployee={temporaryEmployeeAccounts[timeSheet.accountId] ?? false}
				key={timeSheet.accountId}
				onPreview={openEditModal}
				overlaps={accountOverlaps}
				timeSheet={timeSheet}
				timeSheetEntities={accountTimeSheetEntries}
				timeZoneInUse={timeZoneInUse}
				timezoneSourceType={timezoneSourceType}
				workOrderId={workOrderId}
			/>
		);
	}, [
		accountId,
		timeSheetEntriesForTimeline?.timelineEntitiesAndOverlaps,
		associatedWRId,
		isManagerOrAdmin,
		isSuperintendent,
		temporaryEmployeeAccounts,
		timeZoneInUse,
		timezoneSourceType,
		workOrderId,
		openEditModal,
	]);

	if (loading) {
		return <Loading />;
	}

	const disabled = timeSheetInEdit?.superintendentApprovalStatus?.approvalStatus === TimeSheetApprovalStatus.APPROVED;
	const isAllowedToEditTimeSheet = TimeSheetUtils.isUserAllowedToEdit(
		timeSheetInEdit?.accountId === accountId,
		isManagerOrAdmin,
		isSuperintendent,
		isManagementAllowedToEdit
	);

	const isTimeSheetReadonly = !!timeSheetInEdit?.superintendentApprovalStatus?.approvalStatus && TimeSheetUtils.shouldTsBeReadOnly(
		workOrderReviewStatus,
		timeSheetInEdit?.superintendentApprovalStatus?.approvalStatus,
		isAllowedToEditTimeSheet
	);

	if (!timeSheets.length) {

		// if virtual, show different kind of message
		if (!!associatedWRId) {
			return (
				<div className={styles['empty-time-sheet']}>
					There are no time sheets for this report. Please allocate time to this sub-job in order to generate related time sheets.
				</div>
			);
		}
		return (
			<div className={styles['empty-time-sheet']}>
				There are no time sheets for this report. Publish Work Order with employees or temporary employees to generate them.
			</div>
		);
	}

	return (
		<>
			{
				areReportsReadOnly &&
				<div className={styles.banner}>
					In order to edit Time Sheets, please reject Field Report, which will bring it back to edit mode.
				</div>
			}
			{
				!associatedWRId &&
				<div className="time-sheet-list">
					<TimezoneSourceBanner
						changeTimeZone={changeTimeZone}
						showLocalTimeZone={true}
						timezoneInUse={timeZoneInUse}
						timezoneSourceType={timezoneSourceType}
					/>
					{timeSheets.map(renderTimeSheetRow)}
				</div>
			}
			{
				!!associatedWRId &&
				<div className="time-sheet-list">
					{timeSheets.map(renderVirtualTimeSheetRow)}
				</div>
			}
			{!!timeSheetInEdit &&
				<TimeSheetsEditModal
					accessRole={accessRole}
					activeAccounts={activeAccounts}
					areAllTimeSheetsReadOnly={isTimeSheetReadonly || isPaused || disabled}
					areFRsReadOnly={areReportsReadOnly || areReportsFinalOrDone}
					bulkOrSingleEditModal={BulkOrSingleEditModal.SINGLE}
					canManagePerDiem={canManagePerDiem}
					canSeeTimeAllocations={canSeeTimeAllocations}
					classificationCodes={classificationCodes}
					close={closeEditModal}
					code={code}
					dueDate={dueDate}
					hasSIUnassignedPermission={hasSIUnassignedPermission}
					isPaused={isPaused}
					isProjectOrSubjob={isProjectOrSubjob}
					isSuperintendent={isSuperintendent}
					isVirtual={!!associatedWRId}
					loadSubjobsForAllocation={loadSubjobsForAllocation}
					save={onSaveTimeSheetEditing}
					sections={sections}
					showModal={timeSheetInEdit ? true : false}
					subjobsForAllocation={subjobsForAllocation}
					temporaryEmployeeAccounts={temporaryEmployeeAccounts}
					timeSheets={timeSheetInEdit ? [timeSheetInEdit] : null}
					timeSheetsAvailableActions={timeSheetsAvailableActions}
					timeZone={timeZone}
					timeZoneInUse={timeZoneInUse}
					timezoneSourceType={timezoneSourceType}
					workOrderId={workOrderId}
					workOrderReviewLevel={workOrderReviewLevel}
					workOrderReviewStatus={workOrderReviewStatus}
					workRequestId={workRequestId}
				/>
			}
		</>
	);
};

function mapStateToProps(state: RootState) {
	const {
		user: {
			userData,
			companyData,
		},
		company: {
			company,
		},
	} = state;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const { role } = userData;
	const { accountId, isCompanyAdmin, permissions } = companyData;

	return {
		accountId,
		isManagerOrAdmin: isAllowed(PagePermissions.COMPANY.FIELD_REPORT.MANAGE, permissions, isCompanyAdmin, role),
		isManagementAllowedToEdit: company?.isFRManageAllowedToEditTimeSheet ?? false,
		isAccountingOrAdmin: isAllowed(PagePermissions.COMPANY.FIELD_REPORT.MANAGE.ACCOUNTING, permissions, isCompanyAdmin, role),
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(TimeSheets);
